import { useState, useRef, useEffect } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react/dist/ssr';
import { RarityDot } from '@/shared/ui/atoms';
import type { QuestWorkLogItem } from '../../model/types';
import { getCurrentCheckpoint } from '../../lib/helpers';
import * as s from './QuestSummaryNotes.css';
import { useReducedMotion } from '@/shared/lib/hooks';

export interface QuestSummaryNotesProps {
  quests: QuestWorkLogItem[];
  highlightQuestId?: string;
}

export function QuestSummaryNotes({ quests, highlightQuestId }: QuestSummaryNotesProps) {
  const [expandedId, setExpandedId] = useState<string | null>(highlightQuestId ?? null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (highlightQuestId && highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }
  }, [highlightQuestId, reduceMotion]);

  if (quests.length === 0) return null;

  return (
    <div className={s.summaryNotes}>
      {quests.map((quest) => {
        const completed = quest.checkpoints.filter((cp) => cp.completed).length;
        const total = quest.checkpoints.length;
        const isExpanded = expandedId === quest.id;
        const isHighlight = quest.id === highlightQuestId;
        const current = getCurrentCheckpoint(quest.checkpoints);

        return (
          <div
            key={quest.id}
            className={`${s.noteItem}${isHighlight ? ` ${s.noteItemHighlight}` : ''}`}
            ref={isHighlight ? highlightRef : undefined}
          >
            <button
              type="button"
              className={s.noteLine}
              onClick={() => setExpandedId(isExpanded ? null : quest.id)}
              aria-expanded={isExpanded}
            >
              <span className={s.noteToggle}>
                {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
              </span>
              <RarityDot rarity={quest.rarity} />
              <span className={s.noteTitle}>{quest.title}</span>
              <span className={s.noteProgress}>
                {completed}/{total}
              </span>
              {current && !isExpanded && <span className={s.noteCurrent}>{current.title}</span>}
            </button>

            {isExpanded && (
              <div className={s.noteDetail}>
                <ul className={s.checkpointList}>
                  {quest.checkpoints.map((cp) => (
                    <li key={cp.id} className={s.checkpointLine}>
                      <span className={cp.completed ? s.checkpointDone : s.checkpointPending}>
                        {cp.completed ? '\u2713' : '\u25CB'}
                      </span>
                      <span>{cp.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
